import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';

interface AdSlotProps {
  slotId: string;
  width?: number;
  height?: number;
  tags?: string[];
  pageType?: string;
}

// CLS-safe ad slot placeholder. Integrators can hook into window.loadAds to fill.
const AdSlot: React.FC<AdSlotProps> = ({ slotId, width = 300, height = 250, tags = [], pageType }) => {
  const loc = useLocation();

  // When Google AdSense Auto Ads are enabled, we shouldn't render dedicated ad slots at all.
  // Detect via runtime config injected at startup, with optional env override.
  const autoAdsOn = (() => {
    try {
      const win: any = window as any;
      const envFlag = String(process.env.REACT_APP_ADSENSE_AUTO_ADS || '').toLowerCase();
      const envOn = envFlag === '1' || envFlag === 'true' || envFlag === 'yes';
      const cfg = win.__adsenseConfig || {};
      return !!(envOn || (cfg.enabled && cfg.auto_ads));
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (autoAdsOn) return; // no-op when auto ads are handling placement
    try {
      const hook = (window as any).loadAds;
      if (typeof hook === 'function') {
        hook({ slotId, tags, pageType, path: loc.pathname });
      }
    } catch {}
  }, [autoAdsOn, slotId, tags?.join(','), pageType, loc.pathname]);

  if (autoAdsOn) return null;

  return (
    <Box
      id={slotId}
      data-askthebill-ad="1"
      data-tags={(tags || []).join(',')}
      data-page-type={pageType || ''}
      sx={{
        width,
        maxWidth: '100%',
        overflow: 'hidden',
        height,
        minHeight: height,
        border: 'none',
        borderRadius: 1,
        bgcolor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
        fontSize: 12,
        my: 2,
        clear: 'both',
        position: 'relative',
        zIndex: 0,
      }}
      role="complementary"
      aria-label="Sponsored content"
    >
    </Box>
  );
};

export default AdSlot;
